import { useQuery, UseQueryOptions } from '@tanstack/react-query'

import { TANSTACK_QUERY_KEY } from '@/constants/tanstack-query'
import { IHttpError } from '@/data'
import { DURATIONS } from '@/utils'

import {
  ICommissionSummary,
  IReferralClaimHistoryParams,
  IReferralClaimHistoryResponse,
  IReferralQueryParams,
  IReferralStatisticsResponse,
  ReferralModel,
} from '../models'

export function useQueryAccountCommissionSummary(
  params: IReferralQueryParams,
  options?: Omit<
    UseQueryOptions<ICommissionSummary, IHttpError>,
    'queryKey' | 'queryFn' | 'refetchOnWindowFocus' | 'enabled'
  >
) {
  return useQuery<ICommissionSummary, IHttpError>({
    queryKey: [TANSTACK_QUERY_KEY.REFERRAL.ACCOUNT_COMMISSION_SUMMARY, params.walletAddress],
    queryFn: async () => {
      const { data } = await ReferralModel.getAccountCommissionSummary(params)
      return data
    },
    refetchOnWindowFocus: 'always',
    refetchInterval: DURATIONS.ONE_MINUTE_MS,
    enabled: !!params.walletAddress,
    ...options,
  })
}

export function useQueryReferralStatistics(
  params: IReferralQueryParams,
  options?: Omit<
    UseQueryOptions<IReferralStatisticsResponse, IHttpError>,
    'queryKey' | 'queryFn' | 'refetchOnWindowFocus' | 'enabled'
  >
) {
  return useQuery<IReferralStatisticsResponse, IHttpError>({
    queryKey: [TANSTACK_QUERY_KEY.REFERRAL.REFERRAL_STATISTICS, params.walletAddress, params.page, params.limit],
    queryFn: async () => {
      const data = await ReferralModel.getReferralStatistics(params)
      return data
    },
    enabled: !!params.walletAddress,
    refetchOnWindowFocus: 'always',
    ...options,
  })
}

export function useQueryReferralClaimHistory(params: IReferralClaimHistoryParams) {
  return useQuery<IReferralClaimHistoryResponse, IHttpError>({
    queryKey: [TANSTACK_QUERY_KEY.REFERRAL.REFERRAL_CLAIM_HISTORY, params.walletAddress, params.status],
    queryFn: async () => {
      const data = await ReferralModel.getReferralClaimHistory(params)
      return data
    },
    enabled: !!params.walletAddress,
  })
}
